/**
 * The parcel arranger - lets the user move contents between parcels, edit each row's customs fields,
 * and split a multi-quantity row into individual units.
 *
 * It is FULLY CONTROLLED: the plan comes in as a prop and every change goes out through `edit`. It
 * was previously uncontrolled and reseeded itself from its own `consignment` prop whenever that
 * prop's object identity changed, while also reporting changes back up - a two-way binding synced by
 * reference identity. That shape required four coordinating workarounds in the combine flow, and it
 * only avoided an infinite render loop because the reported-up value happened to wrap the same array
 * instance, so the state setter bailed out. Any future clone or normalise step would have made it
 * loop for real.
 *
 * `selected` stays local - it is view state, not part of the plan.
 */
import { ILinkProps, P2GBox, P2GLink, P2GLinkGroup, P2GText } from '@parcel2go/core-ui';
import { useState } from 'react';
import { Country, Customer, Dimensions, InputRuleApiModel, PackageApiModel } from '~/lib/api/smartsend/types';
import { ConsignmentApiModel } from '~/lib/api/smartsend/types';
import { GroupingPanelParcels } from './GroupingPanelParcels';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ParcelArrangeActions, ParcelContentActions } from '~/apps/smartsend/lib/parcels/planEdits';
import { PlanOutcome } from '~/apps/smartsend/lib/parcels/types';
import ConsignmentSummaryParcels from '../panels/consignments/ConsignmentSummaryParcels';

/** Everything the per-row customs fields need, threaded through unchanged. */
export interface ContentFieldContext {
  countries?: Country[];
  productValidation?: InputRuleApiModel[];
  tariffSystem?: string;
  sender?: Customer;
  recipient?: Customer;
  currencyCode?: string;
}

/** Present only when the parcels themselves carry editable dimensions. */
export interface PerParcelDimensions {
  required?: boolean;
  hideIfNotRequired?: boolean;
  getPackages?: () => Promise<PackageApiModel[]>;
}

export interface ParcelArrangerProps {
  plan: ConsignmentApiModel;
  /** Drives the wording only - "Parcel N" vs "Shipment N". */
  outcome: PlanOutcome;
  /** Supply to make the per-row customs fields editable. Absent means read-only fields. */
  edit?: ParcelContentActions;
  /**
   * Supply to let the user move contents between parcels. Absent means no selection checkboxes at
   * all, which is what the flows that only edit contents in place want.
   */
  arrange?: ParcelArrangeActions;
  /**
   * Whether a multi-quantity row may be split into individual units. Off for the flow that resolves
   * rows back to order lines by Id, where two rows sharing an Id would be ambiguous.
   */
  canSplitRows?: boolean;
  perParcelDimensions?: PerParcelDimensions;
  content: ContentFieldContext;
  /** Dimensions used for a parcel the user creates. */
  newParcelDimensions?: Dimensions;
  children?: React.ReactNode;

  /** Loads as a preview without input fields */
  loadPreview?: boolean;

  /**
   * Whether the summary is currently a preview rather than the editor.
   *
   * Optional, and only useful WITH onPreviewChange: supply both to control the toggle from the host,
   * which is what a host needs when it renders something that has to swap with the editor - the
   * shipment-wide dimensions section, which is replaced by the per-box controls inside the editor.
   * Omit both and the arranger owns the toggle itself, seeded from loadPreview.
   */
  preview?: boolean;
  onPreviewChange?: (preview: boolean) => void;

  /**
   * Supply to let the user drag a whole content row between the parcel cards in the PREVIEW.
   *
   * Separate from `arrange` because the preview's plan-owning host has to decide what "everything in
   * one parcel" means for it - Combining switches to its merge radio, the single-item host switches to
   * one box - which is the same decision `ConsignmentBuilder.onCollapseToSingle` already carries.
   * `arrange.moveToParcel` cannot express that.
   */
  onDragRowToParcel?: (contentUID: string, targetParcelUID: string) => void;
}

export const ParcelArranger: React.FC<ParcelArrangerProps> = (props) => {
  const [selected, setSelected] = useState<string[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  // Internal state is kept in step even while the host controls it, so handing control back mid-flow
  // does not snap the view somewhere unexpected.
  const [internalPreview, setInternalPreview] = useState(!!props.loadPreview);
  const preview = props.preview ?? internalPreview;
  const setPreview = (next: boolean) => {
    setInternalPreview(next);
    props.onPreviewChange?.(next);
  };

  const parcels = props.plan?.Parcels ?? [];
  const itemName = props.outcome === 'MANY_SHIPMENTS' ? 'Shipment' : 'Parcel';
  const edit = props.edit;
  const arrange = props.arrange;

  const clearSelection = () => setSelected([]);

  /** True when the selection is exactly one whole parcel - regrouping it would be a no-op. */
  const selectionIsAWholeParcel = () =>
    parcels.filter((p) => p.Contents.length === selected.length && p.Contents.every((o) => selected.includes(o.UID)))
      .length === 1;

  function parcelOptions(): ILinkProps[] {
    const options = parcels
      .filter((p) => p.CanAdd)
      .filter((p) => p.Contents.every((o) => !selected.includes(o.UID)))
      .map((p) => (
        <P2GLink
          key={`move-to-${itemName}-${parcels.indexOf(p)}`}
          onClick={() => {
            arrange?.moveToParcel(p.UID, selected);
            clearSelection();
          }}
        >
          {t('MOVE_TO_PARCEL_LINK', {
            defaultValue: 'Move to {{itemName}} {{number}}',
            itemName,
            number: parcels.indexOf(p) + 1
          })}
        </P2GLink>
      ));

    if (selected.length > 0 && !selectionIsAWholeParcel()) {
      options.push(
        <P2GLink
          key="group-together"
          onClick={() => {
            arrange?.moveToNewParcel(selected);
            clearSelection();
          }}
        >
          {selected.length > 1
            ? t('GROUP_TOGETHER_LINK', { defaultValue: 'Group Together' })
            : t('MOVE_TO_NEW_PARCEL_LINK', { defaultValue: 'Move to New {{itemName}}', itemName })}
        </P2GLink>
      );
    }

    return options;
  }

  if (preview) {
    return (
      <ConsignmentSummaryParcels
        consignment={props.plan}
        currencyCode={props.content.currencyCode ?? ''}
        setExpand={() => setPreview(false)}
        onDragRowToParcel={props.onDragRowToParcel}
      >
        <P2GBox mb={2}>
          <P2GText variant="caption">
            {t('PROPOSED_CONSIGNMENT_ECOMMERCE_SUMMARY_CAPTION', {
              defaultValue: 'Proposed shipment based on your items:'
            })}
          </P2GText>
        </P2GBox>
      </ConsignmentSummaryParcels>
    );
  }

  return (
    <GroupingPanelParcels
      parcels={parcels}
      selected={arrange ? selected : undefined}
      // No arrange actions means no checkboxes at all - nothing could be done with a selection.
      onSelect={
        arrange
          ? (uid) => setSelected(selected.includes(uid) ? selected.filter((x) => x !== uid) : [...selected, uid])
          : undefined
      }
      allowExpanding={props.canSplitRows}
      expand={props.canSplitRows && arrange ? (row) => arrange.expandContent(row.UID) : undefined}
      updateDescription={edit ? (uid, v) => edit.setText(uid, 'Description', v) : undefined}
      updateSku={edit ? (uid, v) => edit.setText(uid, 'Sku', v) : undefined}
      updateTariffCode={edit ? (uid, v) => edit.setText(uid, 'TariffCode', v) : undefined}
      updateCountryOfManufacture={edit ? (uid, v) => edit.setText(uid, 'CountryOfManufacture', v) : undefined}
      updateQuantity={edit ? (uid, v) => edit.setNumber(uid, 'Quantity', v) : undefined}
      updateValue={edit ? (uid, v) => edit.setNumber(uid, 'Value', v) : undefined}
      addContentItem={edit ? (parcelUID) => edit.addManualContent(parcelUID) : undefined}
      removeContentItem={edit ? (parcelUID, itemUID) => edit.removeContent(parcelUID, itemUID) : undefined}
      onParcelDimensionsUpdate={
        edit
          ? (parcelUID, dims, localUnitsEntry) => edit.setParcelDimensions(parcelUID, dims, localUnitsEntry)
          : undefined
      }
      allowInlineDimensions={!!props.perParcelDimensions}
      dimensionsRequired={props.perParcelDimensions?.required}
      hideDimensionsIfNotRequired={props.perParcelDimensions?.hideIfNotRequired}
      getPackages={props.perParcelDimensions?.getPackages}
      countries={props.content.countries}
      productValidation={props.content.productValidation}
      tariffSystem={props.content.tariffSystem}
      sender={props.content.sender}
      recipient={props.content.recipient}
      currencyCode={props.content.currencyCode}
    >
      {selected.length > 0 && (
        <P2GBox p={2} bordered>
          {parcelOptions().length > 0 ? (
            <P2GLinkGroup links={parcelOptions()} />
          ) : (
            <P2GLink>
              {t('ITEMS_SELECTED_LABEL', { defaultValue: '{{count}} items selected', count: selected.length })}
            </P2GLink>
          )}
        </P2GBox>
      )}
    </GroupingPanelParcels>
  );
};
